import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { initMarketingEffects } from "@/lib/marketingParallax";
import "@/styles/marketing-parallax.css";
import { SeoHead } from "@/components/seo/SeoHead";
import {
  Globe,
  Layers,
  ShieldCheck,
  LayoutGrid,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import pfaUsingPhone from "@/assets/pfa-using-phone.jpg";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { LandingStoriesSection } from "@/components/marketing/LandingStoriesSection";
import { GlobalCatererNetworkSection } from "@/components/marketing/GlobalCatererNetworkSection";
import { MarketingSocialSection } from "@/components/marketing/MarketingSocialSection";
import { AppStoreDownloadButton } from "@/components/marketing/AppStoreDownloadButton";
import { buildOrderNowSignupPath } from "@/lib/guestFirstOrder";

/** Same paths as index.html preload — public folder keeps URLs stable across builds. */
const DASHBOARD_HERO_640 = "/images/lcp/dashboard-hero-640.webp";
const DASHBOARD_HERO_896 = "/images/lcp/dashboard-hero-896.webp";

/** Public marketing sections only — no Supabase (smaller initial bundle for anonymous visitors). */
export default function LandingMarketing() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);

  const goOrderNow = () => {
    navigate(buildOrderNowSignupPath());
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    return initMarketingEffects(root);
  }, []);

  return (
    <div ref={rootRef} className="marketing-vitrine min-h-screen bg-background text-foreground scroll-smooth">
      <SeoHead
        title="Kryajet | Private Aviation Catering Platform"
        description="Download Kryajet on iPhone — search aviation caterers by airport, manage orders, messaging, and secure payments for flight attendants and catering teams."
        canonicalPath="/"
      />
      <MarketingHeader />

      <main id="main-content">
      {/* Hero: primary CTA = iOS App Store download */}
      <section
        id="home"
        data-parallax-hero
        className="marketing-hero relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background pb-16 pt-24"
      >
        <div className="marketing-parallax-layer" aria-hidden="true">
          <div data-speed="0.15" data-parallax-mouse className="marketing-blob marketing-blob--accent" />
          <div data-speed="0.35" data-parallax-mouse className="marketing-blob marketing-blob--sky" />
          <div data-speed="0.22" data-parallax-mouse className="marketing-blob marketing-blob--primary" />
        </div>

        <div className="container relative z-10 mx-auto px-6 text-center" data-reveal-section>
          <p
            data-reveal-item
            className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-accent"
          >
            Available on iPhone &amp; iPad
          </p>
          <h1
            data-reveal-item
            className="marketing-display mx-auto mb-6 max-w-4xl text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl"
          >
            Private aviation catering, in your pocket
          </h1>
          <p data-reveal-item className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground md:text-xl">
            Download Kryajet to find caterers by airport, send requests, pay with Apple Pay, and keep every order
            in one thread — built for CFAs on the move.
          </p>
          <div
            data-reveal-item
            className="mb-16 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-5"
          >
            <AppStoreDownloadButton />
            <Button
              size="lg"
              variant="outline"
              onClick={goOrderNow}
              className="text-base"
            >
              Order on the web
            </Button>
          </div>

          <div className="relative mx-auto max-w-4xl" data-reveal-item>
            <div className="marketing-hero-mockup relative" data-parallax-tilt>
              <img
                src={DASHBOARD_HERO_896}
                srcSet={`${DASHBOARD_HERO_640} 640w, ${DASHBOARD_HERO_896} 896w`}
                sizes="(max-width: 896px) 100vw, 896px"
                alt="Kryajet mobile and web experience"
                width={896}
                height={565}
                fetchpriority="high"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <GlobalCatererNetworkSection reveal />

      {/* Why Us Section */}
      <section id="why-us" className="relative overflow-hidden bg-accent py-24">
        <div data-speed="0.18" className="marketing-blob marketing-blob--primary pointer-events-none absolute -right-20 top-10 opacity-25" aria-hidden="true" />
        <div className="container mx-auto px-6" data-reveal-section>
          <div className="mb-16 text-center">
            <h2 data-reveal-item className="marketing-display mb-4 text-3xl font-bold text-accent-foreground md:text-4xl">
              Why Flight Operations Teams Trust Kryajet
            </h2>
            <p data-reveal-item className="mx-auto max-w-3xl text-accent-foreground">
              Unlike emails, WhatsApp messages, and fragmented vendor lists, Kryajet centralizes private aviation catering into one seamless, reliable platform built for operational excellence.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Placeholder Image with overlays */}
            <div className="relative" data-reveal-item>
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-accent-foreground/10">
                <img
                  src={pfaUsingPhone}
                  alt="Corporate flight attendant using Kryajet"
                  width={640}
                  height={896}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                {/* Floating stats overlay */}
                <div className="marketing-kpi-wrap marketing-kpi-wrap--a !bottom-6 !left-6 !right-auto !top-auto">
                  <div className="marketing-kpi-card border-border/80 bg-background/95 p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20">
                        <Globe className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <p className="marketing-kpi-value text-2xl">500+</p>
                        <p className="marketing-kpi-label">Global Caterers</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="marketing-kpi-wrap marketing-kpi-wrap--b !bottom-auto !right-6 !top-6 !left-auto">
                  <div className="marketing-kpi-card border-border/80 bg-background/95 px-4 py-2">
                    <p className="text-sm font-semibold text-foreground">99% Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Feature items */}
            <div className="space-y-6">
              {[
                { 
                  icon: Layers, 
                  title: "All-in-One", 
                  description: "Stop juggling emails, PDFs, and WhatsApp. One platform for sourcing, ordering, payments, and tracking." 
                },
                { 
                  icon: ShieldCheck, 
                  title: "Aviation-Grade Reliability", 
                  description: "Built for time-critical operations. Real-time updates, audit trails, and accountability at every step." 
                },
                { 
                  icon: LayoutGrid, 
                  title: "Curated Global Network", 
                  description: "Access vetted caterers worldwide, filterable by airport, cuisine, certification, and availability." 
                },
                { 
                  icon: Zap, 
                  title: "Operational Control", 
                  description: "From request to delivery, maintain visibility across every order and every leg." 
                },
              ].map((feature, index) => (
                <div key={index} data-reveal-item className="flex gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-accent-foreground/20 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-accent-foreground" />
                    </div>
                  </div>
                  {/* Vertical bar + content */}
                  <div className="flex gap-4">
                    <div className="w-1 bg-accent-foreground/30 rounded-full flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-accent-foreground mb-1">{feature.title}</h3>
                      <p className="text-sm text-accent-foreground">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingStoriesSection />

      <MarketingSocialSection />

      {/* Final CTA Banner */}
      <section className="bg-accent py-24">
        <div className="container mx-auto px-6 text-center" data-reveal-section>
          <h2 data-reveal-item className="marketing-display mb-6 text-3xl font-bold text-accent-foreground md:text-4xl">
            Set a Higher Standard<br />for Onboard Catering
          </h2>
          <p data-reveal-item className="mx-auto mb-8 max-w-2xl text-lg text-accent-foreground">
            Stop relying on fragmented emails, last-minute calls, and inconsistent processes. With Kryajet, you gain clarity, control, and confidence in every catering order anywhere in the world.
          </p>
          <div data-reveal-item>
            <Button size="lg" className="bg-white text-accent hover:bg-white/90" onClick={goOrderNow}>
              Order now
            </Button>
          </div>
        </div>
      </section>

      </main>

      <MarketingFooter />
    </div>
  );
}
